import { useCallback, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { AHText, Box, Card, Input, useBoxStyle } from "arkh";
import { CalendarSubscribeMutation } from "./__generated__/CalendarSubscribeMutation.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { useAlert } from "@/src/common/Alert";
import { FormattedMessage, useIntl } from "react-intl";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { CalendarCreateScreenProps } from "./CalendarCreateNavigation";
import { ScrollView } from "react-native";
import { SizeClass } from "@/src/common/Utils/SizeClass";

export function CalendarSubscribeScreen(
  props: CalendarCreateScreenProps<"CalendarSubscribeScreen">
) {
  const [iCalUrl, setIcalUrl] = useState("");
  const intl_tokens = useIntlTokens();
  const [subscribeToCalendar, subscribeToCalendarLoading] =
    useMutation<CalendarSubscribeMutation>(graphql`
      mutation CalendarSubscribeMutation($input: StoreCalendarSubscribeInput!) {
        store {
          subscribeToCalendar(input: $input) {
            id
            store {
              calendarAddSuggestions {
                iCalUrl
                title
              }
            }
          }
        }
      }
    `);

  const alert = useAlert();
  const intl = useIntl();
  const onCreate = useCallback(() => {
    subscribeToCalendar({
      variables: {
        input: {
          url: iCalUrl,
        },
      },
      onCompleted: (response) => {
        props.navigation.replace("CalendarEdit", {
          calendarId: response.store.subscribeToCalendar.id,
          fromSubscribeToCalendar: true,
        });
      },
      onError: (error) => {
        alert.error(
          intl.formatMessage({
            defaultMessage: "Failed to subscribe to calendar",
            description: "CalendarSubscribe alert error",
          }),
          intl.formatMessage({
            defaultMessage: "Please check the URL",
            description: "CalendarSubscribe alert error message",
          })
        );
      },
    });
  }, [subscribeToCalendar, iCalUrl, props.navigation, alert, intl]);

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  useNavigationHeader({
    type: "CREATE",
    onCancel: () => props.navigation.goBack(),
    onSave: () => {
      onCreate();
    },
    saveLoading: subscribeToCalendarLoading,
    disabled: iCalUrl.length === 0,
    title: isCompact ? "" : intl_tokens.SubscribeToCalendar,
    defaultContentStyle: true,
  });

  const insets = useSafeAreaInsets();
  const contentContainerStyle = useBoxStyle({
    space: "medium",
    insets: {
      bottom: insets.bottom,
    },
  });
  return (
    <ScrollView contentContainerStyle={contentContainerStyle}>
      <CalendarSubscribeToUrl iCalUrl={iCalUrl} setIcalUrl={setIcalUrl} />
    </ScrollView>
  );
}

function CalendarSubscribeToUrl(props: {
  iCalUrl: string;
  setIcalUrl: (iCalUrl: string) => void;
}) {
  const intl_tokens = useIntlTokens();
  return (
    <Box spaceTop="medium">
      <AHText type="title-small" spaceHorizontal="medium" spaceBottom="small">
        {intl_tokens.SubscriptionLink}
      </AHText>
      <Input
        placeholder="example.com/calendar.ics"
        value={props.iCalUrl}
        onChangeText={props.setIcalUrl}
        textContentType="URL"
        keyboardType="url"
      />
      <Box spaceTop="medium" />
      <Card defaultBorder>
        <AHText type="subtitle">
          <FormattedMessage
            defaultMessage="Instructions for linking an Outlook calendar"
            description="CalendarSubscribe instructions title"
          />
        </AHText>
        <AHText
          spaceTop="small"
          style={{
            lineHeight: 20,
          }}
        >
          <FormattedMessage
            defaultMessage='1. Go to Settings (the gear icon in the top right corner).{newLine}2. Select Calendar in the menu on the left.{newLine}3. Go to Shared Calendars.{newLine}4. Under "Publish a calendar", choose the calendar you want to link and select "Can view all details".{newLine}5. Click "Publish".{newLine}6. Click ICS and copy the link.{newLine}7. Paste the link here.'
            description="CalendarSubscribe instructions"
            values={{
              newLine: "\n",
            }}
          />
        </AHText>
      </Card>
    </Box>
  );
}
